import React, { useContext, useEffect } from "react";
import { displayResultMessage, getAvoBotSource } from "utils/misc";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useTheme } from "styled-components";
import { AvoContext } from "analytics/AvoProvider";
import useQuery from "utils/hooks/useQuery";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { MOBILE_SEARCH } from "common/constants";
import { DarkEmail, Email } from "common/Icons/Icons";
import { PrimaryTitle, Wrapper } from "features/auth/styles";
import useMedia from "utils/hooks/useMedia";
import VerificationCode from "features/auth/VerificationCode";
import ResendEmailInstructions from "features/auth/ResendEmailInstructions";
import { OnboardingStep } from "analytics/Avo";
import {
  EMAIL_UUID_QUERY_PARAM,
  REGISTERED_EMAIL,
} from "@shared/common/constants";
import useVerifyEmail from "@shared/features/onboarding/hooks/useVerifyEmail";
import Link from "common/Typography/Link";

const VerifyEmail = () => {
  const { avo } = useContext(AvoContext);
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { appendQuery } = useHistoryWithQuery();
  const query = useQuery();
  const emailValidatorUUID = query.get(EMAIL_UUID_QUERY_PARAM);
  const {
    onResendCode,
    onSubmit,
    showSupportLink,
    codeText,
    resendLoading,
    verificationCodeLoading,
  } = useVerifyEmail({
    emailValidatorUUID,
    displayResultMessage,
    onSubmitSuccess: () => {
      appendQuery(undefined, [EMAIL_UUID_QUERY_PARAM]);
    },
    onResendSuccess: (emailValidatorUUID: string) => {
      appendQuery({
        [EMAIL_UUID_QUERY_PARAM]: emailValidatorUUID,
      });
    },
    storageEmail: sessionStorage.getItem(REGISTERED_EMAIL),
  });

  useEffect(() => {
    const userSource = getAvoBotSource();
    avo?.onboardingVerifyEmailView({
      botSource: userSource,
      onboardingStep: OnboardingStep.EMAIL_CONFIRMATION,
    });
  }, [avo]);

  const Icon = appTheme.isDarkMode ? DarkEmail : Email;
  return (
    <Wrapper $isMobile={isMobile}>
      <LoadingSpinnerOverlay
        isLoading={resendLoading}
        backgroundColor={
          appTheme.isDarkMode
            ? appTheme.colors.layoutBgColor
            : appTheme.colors.cardBgColor
        }
      >
        <PrimaryTitle $isMobile={isMobile}>Email Confirmation</PrimaryTitle>
        <Icon style={{ marginBottom: 24, width: 120, height: 120 }} />
        <Text>{codeText}</Text>
        <VerificationCode
          numOfDigits={NUM_OF_DIGITS_CODE}
          title="Verification Code"
          onSubmit={onSubmit}
          isLoading={verificationCodeLoading}
        />
        <Link
          onClick={() => {
            onResendCode();
            avo?.registrationSendVerifyEmailAgainClicked({
              onboardingStep: OnboardingStep.EMAIL_CONFIRMATION,
            });
          }}
        >
          Resend Email
        </Link>
        {showSupportLink && <ResendEmailInstructions isVerify={true} />}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default VerifyEmail;

const NUM_OF_DIGITS_CODE = 6;
